import React, { useState } from 'react';
import { Button, Input } from './index';
import { BUTTON } from '../../constants';

/**
 * Common Threshold Warning Popup Component
 * Reusable component for displaying threshold warnings across manufacturing, dispatch, receipt, and batch operations
 * 
 * @param {Object} warningData - Warning response from API with status='warning'
 * @param {Function} onConfirm - Callback when user confirms (receives editedValues array or null)
 * @param {Function} onCancel - Callback when user cancels
 * @param {Boolean} allowEdit - Whether quantities are editable (default: true)
 * @param {String} warningType - Type of warning: 'threshold' (default) or 'batch'
 */
export const ThresholdWarningPopup = ({ 
  warningData, 
  onConfirm, 
  onCancel, 
  allowEdit = true,
  warningType = 'threshold' // 'threshold' or 'batch'
}) => {
  const warningDetails = warningData?.data || {};
  
  // Get threshold percentages from warning data for message (produce and damage)
  const getThresholdPercentages = () => {
    let produceThreshold = null;
    let damageThreshold = null;
    
    if (warningDetails.warnings && Array.isArray(warningDetails.warnings)) {
      warningDetails.warnings.forEach(warning => {
        const data = warning.data || warning || {};
        const warningType = data.warning_type || (warning.warning_type === 'damage_threshold_exceeded' ? 'damage' : 'produce');
        
        if (warningType === 'damage') {
          // Damage threshold uses threshold_percentage in the data
          damageThreshold = data.threshold_percentage || warningDetails.damage_threshold_percentage || 10.0;
        } else {
          produceThreshold = data.threshold_percentage || 10.0;
        }
      });
    } else {
      // Single warning case
      const data = warningDetails;
      const warningType = data.warning_type || (warningDetails.warning_type === 'damage_threshold_exceeded' ? 'damage' : 'produce');
      
      if (warningType === 'damage') {
        damageThreshold = data.threshold_percentage || warningDetails.damage_threshold_percentage || 10.0;
      } else {
        produceThreshold = data.threshold_percentage || 10.0;
      }
    }
    
    // Check if damage threshold is in the warning details (for global damage threshold)
    if (!damageThreshold && warningDetails.damage_threshold_percentage) {
      damageThreshold = warningDetails.damage_threshold_percentage;
    }
    
    return { produceThreshold, damageThreshold };
  };
  
  const { produceThreshold, damageThreshold } = getThresholdPercentages();
  
  // For batch threshold warnings (ESUN manufacturing), use detailed table format similar to other manufacturing
  if (warningType === 'batch') {
    const thresholdPercentage = warningDetails.threshold_percentage || 10.0;
    const lot = warningDetails.move_name || 'N/A';
    const productName = warningDetails.product_name || 'N/A';
    const variantSize = warningDetails.variant_size || 'N/A';
    const variantColor = warningDetails.variant_color || 'N/A';
    const orderUnit = warningDetails.ordered_unit || 'Units';
    const demandQty = warningDetails.demand || 0;
    const cutQty = warningDetails.incoming_cut || 0;
    const cutLimit = demandQty * (1 + thresholdPercentage / 100.0);
    const cutDamageQty = warningDetails.incoming_damage || 0;
    const damageLimit = demandQty * (thresholdPercentage / 100.0);
    
    // Check if damage threshold is exceeded
    const damageThresholdExceeded = cutDamageQty > damageLimit;
    
    // Calculate column widths based on whether damage columns are shown
    const baseColumns = 7; // Lot, Product Name, Color, Size, Order Unit, Cut Qty, Limit
    const totalColumns = damageThresholdExceeded ? baseColumns + 2 : baseColumns;
    const columnWidth = 100 / totalColumns;
    
    return (
      <>
        <div className="m-1">
          <div className="d-flex align-items-center justify-content-center mb-2">
            <span className="d-flex justify-content-center align-items-center bg-warning badge me-2" style={{ fontSize: '1rem', padding: '6px 10px' }}>⚠</span>
            <h4 className="text-special mb-0" style={{ fontSize: '16px' }}>Threshold Warning</h4>
          </div>
          <p className="text-center text-special placeholder-color mb-2" style={{ fontSize: '14px' }}>
            Cutting Report threshold exceeded. Allowed threshold: {thresholdPercentage.toFixed(2)}%. Please review the details below.
          </p>
          <div className="mb-2" style={{ maxHeight: '300px', overflowY: 'auto', overflowX: 'auto', scrollbarWidth: 'thin' }}>
            <table className="table table-bordered table-sm mb-0" style={{ width: '100%' }}>
              <thead className="table-light" style={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#f8f9fa' }}>
                <tr>
                  <th className="text-center" style={{ fontSize: '14px', width: `${columnWidth}%` }}>Lot</th>
                  <th className="text-center" style={{ fontSize: '14px', width: `${columnWidth * 2}%` }}>Product Name</th>
                  <th className="text-center" style={{ fontSize: '14px', width: `${columnWidth}%` }}>Color</th>
                  <th className="text-center" style={{ fontSize: '14px', width: `${columnWidth}%` }}>Size</th>
                  <th className="text-center" style={{ fontSize: '14px', width: `${columnWidth}%` }}>Order Unit</th>
                  <th className="text-center" style={{ fontSize: '14px', width: `${columnWidth}%` }}>Cut Qty</th>
                  <th className="text-center" style={{ fontSize: '14px', width: `${columnWidth}%` }}>Limit</th>
                  {damageThresholdExceeded && (
                    <>
                      <th className="text-center" style={{ fontSize: '14px', width: `${columnWidth}%` }}>Cut Damage</th>
                      <th className="text-center" style={{ fontSize: '14px', width: `${columnWidth}%` }}>Limit</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                <tr style={{ position: 'relative' }}>
                  <td 
                    className="text-center align-middle" 
                    style={{ 
                      fontSize: '14px',
                      position: 'relative',
                      borderLeft: damageThresholdExceeded ? '4px solid #dc3545' : '4px solid #ffc107',
                      paddingLeft: '8px',
                      paddingTop: damageThresholdExceeded ? '28px' : '18px',
                      overflow: 'visible'
                    }}
                  >
                    {/* Ribbon badges for produce and damage */}
                    <span
                      style={{
                        position: 'absolute',
                        top: '4px',
                        left: '-4px',
                        minWidth: '60px',
                        width: 'auto',
                        padding: '3px 7px',
                        background: 'linear-gradient(135deg, #ffc107 0%, #e0a800 100%)',
                        color: '#ffffff',
                        fontSize: '9px',
                        fontWeight: '700',
                        textTransform: 'uppercase',
                        textAlign: 'center',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.2)',
                        zIndex: 10,
                        letterSpacing: '0.5px',
                        lineHeight: '1.2',
                        borderRadius: '2px',
                        textShadow: '0 1px 1px rgba(0,0,0,0.2)',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Produce
                    </span>
                    {damageThresholdExceeded && (
                      <span
                        style={{
                          position: 'absolute',
                          top: '20px',
                          left: '-4px',
                          minWidth: '60px',
                          width: 'auto',
                          padding: '3px 7px',
                          background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)',
                          color: '#ffffff',
                          fontSize: '9px',
                          fontWeight: '700',
                          textTransform: 'uppercase',
                          textAlign: 'center',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.2)',
                          zIndex: 9,
                          letterSpacing: '0.5px',
                          lineHeight: '1.2',
                          borderRadius: '2px',
                          textShadow: '0 1px 1px rgba(0,0,0,0.2)',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        Damage
                      </span>
                    )}
                    <span style={{ fontSize: '14px', position: 'relative', zIndex: 2, display: 'block' }}>{lot}</span>
                  </td>
                  <td className="text-center align-middle" style={{ fontSize: '14px', wordWrap: 'break-word', whiteSpace: 'normal', maxWidth: '200px' }}>
                    {productName}
                  </td>
                  <td className="text-center align-middle" style={{ fontSize: '14px' }}>{variantColor}</td>
                  <td className="text-center align-middle" style={{ fontSize: '14px' }}>{variantSize}</td>
                  <td className="text-center align-middle" style={{ fontSize: '14px' }}>
                    {demandQty % 1 === 0 
                      ? demandQty.toFixed(0)
                      : demandQty.toFixed(3).replace(/\.?0+$/, '')} {orderUnit}
                  </td>
                  <td className="text-center align-middle" style={{ fontSize: '14px' }}>
                    {cutQty % 1 === 0 
                      ? cutQty.toFixed(0)
                      : cutQty.toFixed(3).replace(/\.?0+$/, '')}
                  </td>
                  <td className="text-center align-middle" style={{ fontSize: '14px' }}>
                    {cutLimit % 1 === 0 
                      ? cutLimit.toFixed(0)
                      : cutLimit.toFixed(3).replace(/\.?0+$/, '')}
                  </td>
                  {damageThresholdExceeded && (
                    <>
                      <td className="text-center align-middle" style={{ fontSize: '14px' }}>
                        {cutDamageQty % 1 === 0 
                          ? cutDamageQty.toFixed(0)
                          : cutDamageQty.toFixed(3).replace(/\.?0+$/, '')}
                      </td>
                      <td className="text-center align-middle" style={{ fontSize: '14px' }}>
                        {damageLimit % 1 === 0 
                          ? damageLimit.toFixed(0)
                          : damageLimit.toFixed(3).replace(/\.?0+$/, '')}
                      </td>
                    </>
                  )}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <hr style={{ margin: '8px 0' }} />
        <div className="pb-2 pt-1 d-flex align-items-center justify-content-between mx-4 modal-btn custom-button">
          <p className="text-special placeholder-color mb-0" style={{ fontSize: '14px' }}>
            Are you sure you want to continue?
          </p>
          <div className="d-flex align-items-center justify-content-center gap-2">
            <Button
              clickHandler={onConfirm}
              className="border-0 blue-btn d-flex align-items-center justify-content-center py-2 px-3">
              {BUTTON.CONFIRM}
            </Button>
            <Button
              clickHandler={onCancel}
              className="border-0 blue-btn d-flex align-items-center justify-content-center py-2 px-3">
              {BUTTON.CANCEL}
            </Button>
          </div>
        </div>
      </>
    );
  }
  
  // For threshold warnings (produce/damage), use detailed table with editable quantities
  // Prepare table data - handle both single and multiple warnings (produce + damage)
  // Group rows with same lot/product/size/color/demand together
  const getTableData = () => {
    const warnings = warningDetails.warnings && Array.isArray(warningDetails.warnings) 
      ? warningDetails.warnings 
      : [warningDetails];
    
    const operationType = warningDetails.operation_type || 'manufacturing';
    
    // First, collect all warnings with their data
    const rawRows = warnings.map((warning) => {
      const data = warning.data || warning;
      const warningType = data.warning_type || (warning.warning_type === 'damage_threshold_exceeded' ? 'damage' : 'produce');
      
      // Determine type display label based on operation type
      let typeLabel = 'Produce';
      if (warningType === 'damage') {
        typeLabel = 'Damage';
      } else {
        if (operationType === 'dispatch') {
          typeLabel = 'Dispatched';
        } else if (operationType === 'receipt') {
          typeLabel = 'Received';
        } else if (operationType === 'manufacturing') {
          typeLabel = 'Produce';
        }
      }
      
      return {
        moveId: data.move_id,
        lot: data.move_name || data.future_lot || 'N/A',
        type: warningType,
        typeLabel: typeLabel,
        demandQuantity: data.demand_quantity || 0,
        quantity: warningType === 'damage' 
          ? (data.damage_quantity || data.reported_quantity || 0)
          : (data.reported_quantity || 0),
        maxLimit: warningType === 'damage'
          ? (data.allowed_damage_quantity || 0)
          : (data.max_allowed_quantity || 0),
        productName: data.product_name || 'N/A',
        variantSize: data.variant_size || 'N/A',
        variantColor: data.variant_color || 'N/A',
        thresholdPercentage: data.threshold_percentage || 10.0,
      };
    });
    
    // Group rows by lot/product/size/color/demand
    const groupedMap = new Map();
    
    rawRows.forEach(row => {
      const key = `${row.lot}_${row.productName}_${row.variantSize}_${row.variantColor}_${row.demandQuantity}`;
      
      if (!groupedMap.has(key)) {
        groupedMap.set(key, {
          moveId: row.moveId,
          lot: row.lot,
          productName: row.productName,
          variantSize: row.variantSize,
          variantColor: row.variantColor,
          demandQuantity: row.demandQuantity,
          produce: null,
          damage: null,
          thresholdPercentage: row.thresholdPercentage,
        });
      }
      
      const group = groupedMap.get(key);
      if (row.type === 'damage') {
        group.damage = {
          quantity: row.quantity,
          maxLimit: row.maxLimit,
          typeLabel: row.typeLabel,
        };
      } else {
        group.produce = {
          quantity: row.quantity,
          maxLimit: row.maxLimit,
          typeLabel: row.typeLabel,
        };
      }
    });
    
    // Convert grouped map to array
    return Array.from(groupedMap.values());
  };

  const initialTableData = getTableData();
  
  // State to track edited quantities (as strings to allow empty input)
  const [editedQuantities, setEditedQuantities] = useState(() => {
    const acc = {};
    initialTableData.forEach(row => {
      if (row.produce) {
        acc[`${row.moveId}_produce`] = row.produce.quantity.toString();
      }
      if (row.damage) {
        acc[`${row.moveId}_damage`] = row.damage.quantity.toString();
      }
    });
    return acc;
  });

  // Update table data with edited quantities
  const tableData = initialTableData.map(row => {
    const produce = row.produce ? {
      ...row.produce,
      quantity: (() => {
        const key = `${row.moveId}_produce`;
        const qtyStr = editedQuantities[key];
        return qtyStr !== undefined && qtyStr !== '' ? parseFloat(qtyStr) : row.produce.quantity;
      })()
    } : null;
    
    const damage = row.damage ? {
      ...row.damage,
      quantity: (() => {
        const key = `${row.moveId}_damage`;
        const qtyStr = editedQuantities[key];
        return qtyStr !== undefined && qtyStr !== '' ? parseFloat(qtyStr) : row.damage.quantity;
      })()
    } : null;
    
    return {
      ...row,
      produce,
      damage,
    };
  });

  const handleQuantityChange = (moveId, type, value) => {
    const key = `${moveId}_${type}`;
    setEditedQuantities(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleConfirm = () => {
    if (allowEdit) {
      const editedValues = [];
      initialTableData.forEach(row => {
        if (row.produce) {
          const key = `${row.moveId}_produce`;
          const qtyStr = editedQuantities[key];
          const qtyNum = qtyStr !== undefined && qtyStr !== '' ? parseFloat(qtyStr) : row.produce.quantity;
          editedValues.push({
            moveId: row.moveId,
            type: 'produce',
            quantity: isNaN(qtyNum) ? 0 : qtyNum
          });
        }
        if (row.damage) {
          const key = `${row.moveId}_damage`;
          const qtyStr = editedQuantities[key];
          const qtyNum = qtyStr !== undefined && qtyStr !== '' ? parseFloat(qtyStr) : row.damage.quantity;
          editedValues.push({
            moveId: row.moveId,
            type: 'damage',
            quantity: isNaN(qtyNum) ? 0 : qtyNum
          });
        }
      });
      onConfirm(editedValues);
    } else {
      onConfirm(null);
    }
  };
  
  return (
    <>
      <div className="m-1">
        <div className="d-flex align-items-center justify-content-center mb-2">
          <span className="d-flex justify-content-center align-items-center bg-warning badge me-2" style={{ fontSize: '1rem', padding: '6px 10px' }}>⚠</span>
          <h4 className="text-special mb-0" style={{ fontSize: '16px' }}>Threshold Warning</h4>
        </div>
        <p className="text-center text-special placeholder-color mb-2" style={{ fontSize: '14px' }}>
          {(() => {
            const operationType = warningDetails.operation_type || 'manufacturing';
            const operationName = operationType === 'dispatch' ? 'Dispatch' : 
                                  operationType === 'receipt' ? 'Receipt' : 'Manufacturing';
            
            if (produceThreshold !== null && damageThreshold !== null) {
              return `${operationName} threshold exceeded. Allowed thresholds: ${operationName.toLowerCase()} ${produceThreshold.toFixed(2)}%, Damage ${damageThreshold.toFixed(2)}%. Please review the details below.`;
            } else if (produceThreshold !== null) {
              return `${operationName} threshold exceeded. Allowed threshold: ${produceThreshold.toFixed(2)}%. Please review the details below.`;
            } else if (damageThreshold !== null) {
              return `Damage threshold exceeded. Allowed threshold: ${damageThreshold.toFixed(2)}%. Please review the details below.`;
            } else {
              return `Threshold exceeded. Please review the details below.`;
            }
          })()}
        </p>
        <div className="mb-2" style={{ maxHeight: '300px', overflowY: 'auto', overflowX: 'auto', scrollbarWidth: 'thin' }}>
          <table className="table table-bordered table-sm mb-0" style={{ width: '100%' }}>
            <thead className="table-light" style={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#f8f9fa' }}>
              <tr>
                <th className="text-center" style={{ fontSize: '14px', width: '12%' }}>Lot</th>
                <th className="text-center" style={{ fontSize: '14px', width: '25%' }}>Product</th>
                <th className="text-center" style={{ fontSize: '14px', width: '8%' }}>Size</th>
                <th className="text-center" style={{ fontSize: '14px', width: '8%' }}>Color</th>
                <th className="text-center" style={{ fontSize: '14px', width: '9%' }}>Demand</th>
                {tableData.some(row => row.produce && row.damage) ? (
                  <>
                    <th className="text-center" style={{ fontSize: '14px', width: '12%' }} colSpan={2}>
                      {warningDetails.operation_type === 'dispatch' ? 'Dispatched' : 
                       warningDetails.operation_type === 'receipt' ? 'Received' : 
                       warningDetails.operation_type === 'manufacturing' ? 'Produce' : 'Produce'}
                    </th>
                    <th className="text-center" style={{ fontSize: '14px', width: '12%' }} colSpan={2}>Damage</th>
                  </>
                ) : (
                  <>
                    <th className="text-center" style={{ fontSize: '14px', width: '12%' }}>Quantity</th>
                    <th className="text-center" style={{ fontSize: '14px', width: '12%' }}>Limit</th>
                  </>
                )}
              </tr>
              {tableData.some(row => row.produce && row.damage) && (
                <tr>
                  <th colSpan="5"></th>
                  <th className="text-center" style={{ fontSize: '12px', padding: '4px' }}>Quantity</th>
                  <th className="text-center" style={{ fontSize: '12px', padding: '4px' }}>Limit</th>
                  <th className="text-center" style={{ fontSize: '12px', padding: '4px' }}>Quantity</th>
                  <th className="text-center" style={{ fontSize: '12px', padding: '4px' }}>Limit</th>
                </tr>
              )}
            </thead>
            <tbody>
              {tableData.map((row, index) => {
                // Determine border color based on what types are present
                const hasDamage = !!row.damage;
                const borderColor = hasDamage ? '#dc3545' : '#ffc107';
                
                // Get all ribbon badges needed
                const ribbons = [];
                if (row.produce) {
                  ribbons.push({ label: row.produce.typeLabel, color: '#ffc107' });
                }
                if (row.damage) {
                  ribbons.push({ label: row.damage.typeLabel, color: '#dc3545' });
                }
                
                return (
                  <tr key={index} style={{ position: 'relative' }}>
                    <td 
                      className="text-center align-middle" 
                      style={{ 
                        fontSize: '14px',
                        position: 'relative',
                        borderLeft: `4px solid ${borderColor}`,
                        paddingLeft: '8px',
                        paddingTop: ribbons.length > 1 ? '28px' : '18px',
                        overflow: 'visible'
                      }}
                    >
                      {/* Multiple ribbon badges if both produce and damage */}
                      {ribbons.map((ribbon, ribbonIndex) => (
                        <span
                          key={ribbonIndex}
                          style={{
                            position: 'absolute',
                            top: `${4 + (ribbonIndex * 16)}px`,
                            left: '-4px',
                            minWidth: '60px',
                            width: 'auto',
                            padding: '3px 7px',
                            background: `linear-gradient(135deg, ${ribbon.color} 0%, ${ribbon.color === '#dc3545' ? '#c82333' : '#e0a800'} 100%)`,
                            color: '#ffffff',
                            fontSize: '9px',
                            fontWeight: '700',
                            textTransform: 'uppercase',
                            textAlign: 'center',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.2)',
                            zIndex: 10 - ribbonIndex,
                            letterSpacing: '0.5px',
                            lineHeight: '1.2',
                            borderRadius: '2px',
                            textShadow: '0 1px 1px rgba(0,0,0,0.2)',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {ribbon.label}
                        </span>
                      ))}
                      <span style={{ fontSize: '14px', position: 'relative', zIndex: 2, display: 'block' }}>{row.lot}</span>
                    </td>
                    <td className="text-center align-middle" style={{ fontSize: '14px', wordWrap: 'break-word', whiteSpace: 'normal', maxWidth: '200px' }}>
                      {row.productName}
                    </td>
                    <td className="text-center align-middle" style={{ fontSize: '14px' }}>{row.variantSize}</td>
                    <td className="text-center align-middle" style={{ fontSize: '14px' }}>{row.variantColor}</td>
                    <td className="text-center align-middle" style={{ fontSize: '14px' }}>
                      {row.demandQuantity % 1 === 0 
                        ? row.demandQuantity.toFixed(0)
                        : row.demandQuantity.toFixed(3).replace(/\.?0+$/, '')}
                    </td>
                    
                    {/* Determine if we have mixed warnings (both produce and damage) */}
                    {tableData.some(r => r.produce && r.damage) ? (
                      <>
                        {/* Mixed warnings: Show produce columns */}
                        {row.produce ? (
                          <>
                            <td className="text-center align-middle" style={{ fontSize: '14px', borderRight: '1px solid #dee2e6' }}>
                              {allowEdit ? (
                                <Input
                                  type="number"
                                  value={editedQuantities[`${row.moveId}_produce`] !== undefined ? editedQuantities[`${row.moveId}_produce`] : row.produce.quantity.toString()}
                                  onChange={(e) => handleQuantityChange(row.moveId, 'produce', e.target.value)}
                                  className="form-control form-control-sm text-center"
                                  style={{ width: '90px', margin: '0 auto', fontSize: '14px' }}
                                  min="0"
                                  step="0.01"
                                  onWheel={(e) => e.target.blur()}
                                />
                              ) : (
                                <span className="text-special" style={{ fontSize: '14px' }}>{row.produce.quantity.toFixed(2)}</span>
                              )}
                            </td>
                            <td className="text-center align-middle" style={{ fontSize: '14px', borderRight: '1px solid #dee2e6' }}>
                              {row.produce.maxLimit.toFixed(2)}
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="text-center align-middle" style={{ fontSize: '14px', borderRight: '1px solid #dee2e6' }}>
                              -
                            </td>
                            <td className="text-center align-middle" style={{ fontSize: '14px', borderRight: '1px solid #dee2e6' }}>
                              -
                            </td>
                          </>
                        )}
                        
                        {/* Mixed warnings: Show damage columns */}
                        {row.damage ? (
                          <>
                            <td className="text-center align-middle" style={{ fontSize: '14px' }}>
                              {allowEdit ? (
                                <Input
                                  type="number"
                                  value={editedQuantities[`${row.moveId}_damage`] !== undefined ? editedQuantities[`${row.moveId}_damage`] : row.damage.quantity.toString()}
                                  onChange={(e) => handleQuantityChange(row.moveId, 'damage', e.target.value)}
                                  className="form-control form-control-sm text-center"
                                  style={{ width: '90px', margin: '0 auto', fontSize: '14px' }}
                                  min="0"
                                  step="0.01"
                                  onWheel={(e) => e.target.blur()}
                                />
                              ) : (
                                <span className="text-special" style={{ fontSize: '14px' }}>{row.damage.quantity.toFixed(2)}</span>
                              )}
                            </td>
                            <td className="text-center align-middle" style={{ fontSize: '14px' }}>
                              {row.damage.maxLimit.toFixed(2)}
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="text-center align-middle" style={{ fontSize: '14px' }}>
                              -
                            </td>
                            <td className="text-center align-middle" style={{ fontSize: '14px' }}>
                              -
                            </td>
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        {/* Single type warnings: Show in main Quantity/Limit columns */}
                        {row.produce ? (
                          <>
                            <td className="text-center align-middle" style={{ fontSize: '14px' }}>
                              {allowEdit ? (
                                <Input
                                  type="number"
                                  value={editedQuantities[`${row.moveId}_produce`] !== undefined ? editedQuantities[`${row.moveId}_produce`] : row.produce.quantity.toString()}
                                  onChange={(e) => handleQuantityChange(row.moveId, 'produce', e.target.value)}
                                  className="form-control form-control-sm text-center"
                                  style={{ width: '90px', margin: '0 auto', fontSize: '14px' }}
                                  min="0"
                                  step="0.01"
                                  onWheel={(e) => e.target.blur()}
                                />
                              ) : (
                                <span className="text-special" style={{ fontSize: '14px' }}>{row.produce.quantity.toFixed(2)}</span>
                              )}
                            </td>
                            <td className="text-center align-middle" style={{ fontSize: '14px' }}>
                              {row.produce.maxLimit.toFixed(2)}
                            </td>
                          </>
                        ) : row.damage ? (
                          <>
                            <td className="text-center align-middle" style={{ fontSize: '14px' }}>
                              {allowEdit ? (
                                <Input
                                  type="number"
                                  value={editedQuantities[`${row.moveId}_damage`] !== undefined ? editedQuantities[`${row.moveId}_damage`] : row.damage.quantity.toString()}
                                  onChange={(e) => handleQuantityChange(row.moveId, 'damage', e.target.value)}
                                  className="form-control form-control-sm text-center"
                                  style={{ width: '90px', margin: '0 auto', fontSize: '14px' }}
                                  min="0"
                                  step="0.01"
                                  onWheel={(e) => e.target.blur()}
                                />
                              ) : (
                                <span className="text-special" style={{ fontSize: '14px' }}>{row.damage.quantity.toFixed(2)}</span>
                              )}
                            </td>
                            <td className="text-center align-middle" style={{ fontSize: '14px' }}>
                              {row.damage.maxLimit.toFixed(2)}
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="text-center align-middle" style={{ fontSize: '14px' }}>
                              -
                            </td>
                            <td className="text-center align-middle" style={{ fontSize: '14px' }}>
                              -
                            </td>
                          </>
                        )}
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <hr style={{ margin: '8px 0' }} />
      <div className="pb-2 pt-1 d-flex align-items-center justify-content-between mx-4 modal-btn custom-button">
        <p className="text-special placeholder-color mb-0" style={{ fontSize: '14px' }}>
          Are you sure you want to continue?
        </p>
        <div className="d-flex align-items-center justify-content-center gap-2">
          <Button
            clickHandler={handleConfirm}
            className="border-0 blue-btn d-flex align-items-center justify-content-center py-2 px-3">
            {BUTTON.CONFIRM}
          </Button>
          <Button
            clickHandler={onCancel}
            className="border-0 blue-btn d-flex align-items-center justify-content-center py-2 px-3">
            {BUTTON.CANCEL}
          </Button>
        </div>
      </div>
    </>
  );
};
